"use client";

import { Mail, Phone, MapPin } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-primary text-foreground-primary border border-primary/20 rounded-lg">
        <p className="font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-foreground-primary text-foreground-primary hover:bg-foreground-primary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="animate-fade-in-up" style={{
      animationDelay: "0ms"
    }} data-index={0}>
            <FormLabel className="text-foreground">Name</FormLabel>
            <FormControl>
              <Input placeholder="Your Name" {...field} className="bg-background border-border focus:border-primary focus:ring-primary/50 transition-colors duration-[var(--duration-fast)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem className="animate-fade-in-up" style={{
      animationDelay: "120ms"
    }} data-index={1}>
            <FormLabel className="text-foreground">Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} className="bg-background border-border focus:border-primary focus:ring-primary/50 transition-colors duration-[var(--duration-fast)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone is required",
      pattern: {
        value: /^[0-9\s\-\+\(\)]+$/,
        message: "Invalid phone number"
      }
    }} render={({
      field
    }) => <FormItem className="animate-fade-in-up" style={{
      animationDelay: "240ms"
    }} data-index={2}>
            <FormLabel className="text-foreground">Phone</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="0207 362 7788" {...field} className="bg-background border-border focus:border-primary focus:ring-primary/50 transition-colors duration-[var(--duration-fast)]" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="animate-fade-in-up" style={{
      animationDelay: "360ms"
    }} data-index={3}>
            <FormLabel className="text-foreground">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your management recruitment needs..." {...field} className="bg-background border-border focus:border-primary focus:ring-primary/50 transition-colors duration-[var(--duration-fast)] min-h-32" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full btn-primary animate-fade-in-up shadow-xs" style={{
      animationDelay: "480ms"
    }}>
        {isSubmitting ? <>
            <span className="animate-spin mr-2">⏳</span>
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function GetInTouch() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const {
    ref: iconRef1,
    isVisible: icon1Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const {
    ref: iconRef2,
    isVisible: icon2Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  const {
    ref: iconRef3,
    isVisible: icon3Visible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.5
  });
  return <section ref={sectionRef} className="relative py-24 px-6 bg-page-background overflow-hidden" style={{
    backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.02) 35px, rgba(255,255,255,.02) 70px)"
  }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground-page mb-4">
            Get in Touch
          </h2>
          <p className="text-lg text-foreground-page/80 max-w-2xl mx-auto">
            Ready to activate your recruitment strategy? Contact us today and let's discuss how we can help you find the right talent.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_auto] gap-12 items-start">
          <div className={`content-block transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${sectionVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <FormProvider formId="contact-form-get-in-touch" sectionName="get-in-touch" defaultValues={{
            name: "",
            email: "",
            phone: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 50
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 500
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you within 24 hours.">
              <ContactForm />
            </FormProvider>
          </div>

          <div className="flex flex-col gap-8 md:min-w-[240px]">
            <div ref={iconRef1} className={`flex items-center gap-4 text-foreground-page transition-all duration-[var(--duration-medium)] ${icon1Visible ? "animate-pulse-once" : "opacity-0"}`} data-index={0}>
              <div className="flex-shrink-0 w-12 h-12 bg-accent/30 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="font-semibold text-sm mb-1">Email</p>
                <p className="text-sm text-foreground-page/80">info@cohesionrecruitment.com</p>
              </div>
            </div>

            <div ref={iconRef2} className={`flex items-center gap-4 text-foreground-page transition-all duration-[var(--duration-medium)] ${icon2Visible ? "animate-pulse-once" : "opacity-0"}`} style={{
            animationDelay: "150ms"
          }} data-index={1}>
              <div className="flex-shrink-0 w-12 h-12 bg-secondary/30 rounded-full flex items-center justify-center">
                <Phone className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-sm mb-1">Phone</p>
                <p className="text-sm text-foreground-page/80">0121 713 8320</p>
              </div>
            </div>

            <div ref={iconRef3} className={`flex items-center gap-4 text-foreground-page transition-all duration-[var(--duration-medium)] ${icon3Visible ? "animate-pulse-once" : "opacity-0"}`} style={{
            animationDelay: "300ms"
          }} data-index={2}>
              <div className="flex-shrink-0 w-12 h-12 bg-primary/30 rounded-full flex items-center justify-center">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm mb-1">Location</p>
                <p className="text-sm text-foreground-page/80">Stratford Court, Cranmore Boulevard, Solihull B90 4QT</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse-once {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        .animate-pulse-once {
          animation: pulse-once 600ms ease-out;
        }
      `}</style>
    </section>;
}